import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, ClipboardCheck, Download, FileSignature, Loader2, Mail, Truck, XCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { api, apiClient, getApiErrorMessage } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import BolDocument from '../../components/trucking/BolDocument';
import LoadRatesPanel from '../../components/trucking/LoadRatesPanel';
import LoadEquipmentPanel from '../../components/trucking/LoadEquipmentPanel';
import LoadDriverPanel from '../../components/trucking/LoadDriverPanel';
import TruckingInvoiceCard from '../../components/trucking/TruckingInvoiceCard';
import { bolCoverage } from '../../lib/trucking-invoice-status';
import { readRefusals } from '../../lib/load-rates';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure, retryUnlessRefused } from '../../lib/load-failure';
import { unwrap } from '../../lib/api-envelope';
import { loadStatusSkin } from '../../lib/load-status';
// signed_at / cancelled_at are `timestamp with timezone` in the API schema —
// instants, so the day they fall on is the tenant's. formatInstantDay, not
// formatCalendarDate; the BOL's own scheduled_date is the calendar-day one.
import { formatCalendarDate, formatInstantDay } from '../../lib/calendar-date';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { toast } from 'sonner';
import { Tooltip } from '../../components/ui/tooltip';

/**
 * One bill of lading (one load), from GET /trucking/loads/bols/:id.
 *
 * GONE: "Create Invoice", which opened /invoices/new, a path with no route; and
 * "Download PDF" opening `pdf_url`, a column nothing writes, so it was always
 * disabled. Download PDF now saves the API's rendered PDF, the same file the
 * driver's BOL view saves (#803, #810). "Email to AP" was a blank email to the
 * customer's main contact, not to accounts payable; it says what it does now.
 *
 * The Rates panel (api#950) shows the load's billing, what the rate card
 * filled and what was typed, and edits them until the BOL is signed.
 *
 * The Invoice panel (api#954) says which invoice bills this BOL, or "No
 * invoice" with Generate Invoice: how the office meets a signature's refused
 * auto-draft. It also carries the "Mob fee already covers this trip" line
 * from the load's `rental_fee_overlap` (api 0496).
 */

const CANCELLABLE_STATUSES = ['booked', 'dispatched', 'en_route', 'arrived'];

const statusOf = (e: unknown) => (e as { response?: { status?: number } } | null)?.response?.status;
const refusedOrUnreachableRetry = retryUnlessRefused();

/** A calendar day as a month name ("Oct 2, 2026"); a bare numeric day is ambiguous across locales. */
const fmtDay = (d: unknown) => formatCalendarDate(typeof d === 'string' ? d : null) ?? '—';

export default function BillOfLadingDetail() {
  const timeZone = useTenantTimeZone();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { company } = useAuth();
  // New Load lands here with the create response's `rate_card_refusals`: the
  // API returns them on that response only, so they come in router state.
  const location = useLocation();
  const bookedRefusals = readRefusals({
    rate_card_refusals: (location.state as { rateCardRefusals?: unknown } | null)?.rateCardRefusals,
  });
  const queryClient = useQueryClient();

  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelRefusal, setCancelRefusal] = useState<string | null>(null);
  // As typed. Blank is "no charge" and is not sent; "0" is sent and waives it.
  const [dryRunCharge, setDryRunCharge] = useState('');
  const [downloading, setDownloading] = useState(false);

  const bolQuery = useQuery({
    queryKey: ['bol-detail', id],
    queryFn: () => apiClient.get(`/trucking/loads/bols/${id}`).then(r => unwrap<any>(r.data) ?? null),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
    // A 404 is an answer too: this BOL does not exist for this company.
    retry: (n: number, e: unknown) => statusOf(e) !== 404 && refusedOrUnreachableRetry(n, e),
  });
  const notFound = statusOf(queryFailureError(bolQuery)) === 404 || (bolQuery.isSuccess && !bolQuery.data);
  const failed = !notFound && queryLoadFailure(bolQuery) != null;
  const loading = bolQuery.isLoading && !failed && !notFound;
  const bol = failed || notFound ? undefined : bolQuery.data;
  // A dry run is a truck that left the yard: only a departed load can carry one.
  const departed = !!bol?.departed_at;

  const cancelMutation = useMutation({
    // A reason is required (fe#815 cancel): the API answers 422
    // LOAD_CANCEL_REASON_REQUIRED without one, and the button stays off until
    // there is one. A cancelled load is final and keeps its BOL number.
    // The dry run charge is whatever the field holds; the field is only shown
    // for a departed load and is emptied each time the dialog opens.
    mutationFn: () => api.trucking.loads.cancel(id!, cancelReason.trim(), dryRunCharge),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['bol-detail', id] });
      queryClient.invalidateQueries({ queryKey: ['trucking-bols'] });
      queryClient.invalidateQueries({ queryKey: ['trucking-loads'] });
      queryClient.invalidateQueries({ queryKey: ['trucking-dispatch-alerts'] });
      setCancelOpen(false);
      setCancelReason('');
      setCancelRefusal(null);
      setDryRunCharge('');
      // The invoice number is the API's: the dry run line is on a new draft.
      // A toast with an action stays until it is clicked or dismissed (its own
      // close button): an auto-closing one takes the only link to the invoice
      // away before the office can reach it.
      const invoice = res?.data?.dry_run_invoice;
      if (invoice) {
        toast.success(`Load cancelled. Dry run billed on draft invoice ${invoice.invoice_number}.`, {
          duration: Infinity,
          closeButton: true,
          action: { label: 'Open invoice', onClick: () => navigate(`/trucking/invoices/${invoice.id}`) },
        });
      } else {
        toast.success('Load cancelled');
      }
    },
    // The API's words, in the dialog: LOAD_NOT_CANCELLABLE says why ("…because
    // it is signed. Void and rebill instead.").
    onError: (err: unknown) => setCancelRefusal(getApiErrorMessage(err, 'The load was not cancelled.')),
  });

  /** The PDF the API renders, saved as a file. A failure says so. */
  const handleDownloadPdf = async () => {
    if (!id) return;
    setDownloading(true);
    try {
      const res = await api.trucking.bols.pdf(id);
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${bol?.bol_number || 'BOL'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Couldn't generate the PDF. Nothing was downloaded.");
    } finally {
      setDownloading(false);
    }
  };

  const canCancel = bol != null && CANCELLABLE_STATUSES.includes(bol.status);
  const statusLabel = bol ? loadStatusSkin(bol.status).label : '—';
  const signedOn = bol?.signed_at ? formatInstantDay(bol.signed_at, timeZone) ?? '—' : null;
  const cancelledOn = bol?.cancelled_at ? formatInstantDay(bol.cancelled_at, timeZone) ?? '—' : null;
  const secondary = 'fi-action fi-action--standard border border-white/25 hover:border-white/60 disabled:cursor-not-allowed disabled:opacity-50';

  return (
    <div data-testid="bol-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Bill of lading"
        title={bol ? `BOL ${bol.bol_number || '—'}` : notFound ? 'BOL not found' : 'Bill of Lading'}
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          bol ? (
            <>
              <Tooltip content={bol.customer_email ? undefined : 'This customer has no contact email'}>
                <button
                  type="button"
                  className={secondary}
                  onClick={() => window.open(`mailto:${bol.customer_email}?subject=BOL ${bol.bol_number}`, '_blank')}
                  disabled={!bol.customer_email}
                >
                  <Mail className="size-4" aria-hidden="true" />
                  Email customer
                </button>
              </Tooltip>
              <button type="button" className={secondary} onClick={handleDownloadPdf} disabled={downloading}>
                {downloading ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <Download className="size-4" aria-hidden="true" />}
                Download PDF
              </button>
              {canCancel && (
                <button type="button" className={secondary} onClick={() => { setDryRunCharge(''); setCancelOpen(true); }}>
                  <XCircle className="size-4" aria-hidden="true" />
                  Cancel Load
                </button>
              )}
            </>
          ) : undefined
        }
        stats={[
          { icon: <ClipboardCheck className="size-4" aria-hidden="true" />, label: 'Status', sub: 'Of the load', tone: 'neutral', loading, value: statusLabel },
          { icon: <CalendarClock className="size-4" aria-hidden="true" />, label: 'Scheduled', sub: 'Load date', tone: 'info', loading, value: bol ? fmtDay(bol.scheduled_date) : '—' },
          { icon: <Truck className="size-4" aria-hidden="true" />, label: 'Truck', sub: 'Unit number', tone: 'accent', loading, value: bol?.truck_unit_number || '—' },
          // Not the driver's name: a full name overruns the band's last column at
          // phone width, and the document below prints the driver. The signed day
          // is a date, the same width as Scheduled.
          { icon: <FileSignature className="size-4" aria-hidden="true" />, label: 'Signed', sub: 'By the customer', tone: 'success', loading, value: signedOn ?? '—' },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {failed ? (
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
              <LoadFailureNotice error={queryFailureError(bolQuery)} subject="this bill of lading" />
            </div>
          ) : notFound ? (
            <p className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-12 text-center text-sm text-fieldiq-muted">
              This bill of lading doesn&rsquo;t exist, or isn&rsquo;t one of your company&rsquo;s.
            </p>
          ) : loading || !bol ? (
            <div className="h-64 animate-pulse rounded-fi-panel bg-fieldiq-card" aria-hidden="true" />
          ) : (
            <>
              {(signedOn || cancelledOn) && (
                <p className="flex flex-wrap gap-x-3 text-xs text-fieldiq-muted">
                  {signedOn && <span>Signed {signedOn}</span>}
                  {cancelledOn && <span>Cancelled {cancelledOn}</span>}
                </p>
              )}

              {bol.status === 'cancelled' && bol.cancel_reason && (
                <div data-testid="cancel-reason" className="rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-red bg-fieldiq-card px-4 py-3">
                  <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Cancellation reason</p>
                  <p className="mt-1 text-sm text-fieldiq-text">{bol.cancel_reason}</p>
                </div>
              )}

              {/* Generate Invoice and the mob / demob fee note. Not on a cancelled load: it is never invoiced. */}
              {bol.status !== 'cancelled' && (
                <TruckingInvoiceCard
                  target={{ kind: 'load', id: bol.id, ready: !!bol.signed_at, coverage: bolCoverage(bol), billedOnRental: bol.billed_on_rental === true }}
                  overlap={bol.rental_fee_overlap}
                />
              )}

              <LoadEquipmentPanel load={bol} onSaved={() => queryClient.invalidateQueries({ queryKey: ['bol-detail', id] })} />

              {/* The load's driver. A change also moves the Dispatch board, the
                  calendar and the drivers' "on a load" status, so those reads
                  are refreshed with it. */}
              <LoadDriverPanel
                load={bol}
                onSaved={() => {
                  queryClient.invalidateQueries({ queryKey: ['bol-detail', id] });
                  queryClient.invalidateQueries({ queryKey: ['trucking-loads'] });
                  queryClient.invalidateQueries({ queryKey: ['trucking-driver-options'] });
                  queryClient.invalidateQueries({ queryKey: ['trucking-driver-availability'] });
                }}
              />

              <LoadRatesPanel
                load={bol}
                initialRefusals={bookedRefusals}
                onSaved={() => queryClient.invalidateQueries({ queryKey: ['bol-detail', id] })}
              />

              <BolDocument bol={bol} companyName={company?.name} />
            </>
          )}
        </div>
      </section>

      {/* Cancel confirmation modal */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel this load?</DialogTitle>
          </DialogHeader>
          <p className="mb-4 text-sm text-fieldiq-muted">
            This sets the status to cancelled and keeps the BOL number. Payroll is not affected (no payroll was triggered). This cannot be undone.
          </p>
          <div>
            <label htmlFor="cancel-reason-input" className="mb-1 block text-xs font-medium text-fieldiq-muted">Reason (required)</label>
            <textarea
              id="cancel-reason-input"
              value={cancelReason}
              onChange={e => { setCancelReason(e.target.value.slice(0, 500)); setCancelRefusal(null); }}
              maxLength={500}
              rows={3}
              placeholder="Why is this load being cancelled?"
              className="w-full resize-none rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-3 py-2 text-sm text-fieldiq-text placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none"
            />
            <p className="mt-1 text-right text-[10px] text-fieldiq-muted">{cancelReason.length}/500</p>
          </div>
          {departed && (
            <div>
              <label htmlFor="dry-run-charge-input" className="mb-1 block text-xs font-medium text-fieldiq-muted">Dry run charge (optional)</label>
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-fieldiq-muted" aria-hidden="true">$</span>
                <input
                  id="dry-run-charge-input"
                  type="text"
                  inputMode="decimal"
                  value={dryRunCharge}
                  onChange={e => { setDryRunCharge(e.target.value); setCancelRefusal(null); }}
                  placeholder="No charge"
                  aria-describedby="dry-run-charge-help"
                  className="w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-card py-2 pl-7 pr-3 text-sm text-fieldiq-text placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none"
                />
              </div>
              <p id="dry-run-charge-help" className="mt-1 text-xs text-fieldiq-muted">
                The truck left the yard. Leave blank for no charge. Enter 0 to record the dry run as waived. Any other amount is billed on a new draft invoice.
              </p>
            </div>
          )}
          {cancelRefusal && (
            <p role="alert" data-testid="cancel-refusal" className="text-sm font-medium text-fieldiq-text">{cancelRefusal}</p>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setCancelOpen(false)} disabled={cancelMutation.isPending}>
              Keep load
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={() => cancelMutation.mutate()}
              disabled={cancelMutation.isPending || !cancelReason.trim()}
            >
              {cancelMutation.isPending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
              Cancel load
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
